import React from 'react'
import { createNgReactBridgeDirective } from '../../react-bridge'
import {
  ApplicationTooltipContent,
  ServerApplicationTooltipContent,
  ApplicationLinkWithTooltip
} from '../../../components/application-tooltip'
import { ApplicationImportance } from '../../../components/application-importance'
import {
  Preconditions,
  Predicates,
  Predicate,
  isApplicationImportance
} from '../validation'
import { compose } from '../../../util'
import { injectIntl } from 'react-intl'

type MetadataFieldType = 'STRING' | 'NUMERIC' | 'PERSON_OF_CONTACT'

type MetadataSubfieldType = 'CONTACT_NAME' | 'PHONE' | 'EMAIL'

interface MetadataEntity {
  readonly fieldName: string
  readonly fieldValue: string
  readonly unique: boolean
  readonly type: MetadataFieldType
  readonly subfields?: Array<{
    readonly fieldName: string
    readonly fieldValue: string
    readonly unique: boolean
    readonly type: MetadataSubfieldType
  }>
}

const isMetadataEntity: Predicate<MetadataEntity> = Predicates.hasShape({
  fieldName: Predicates.isString,
  fieldValue: Predicates.isString,
  unique: Predicates.isBoolean,
  type: Predicates.getLiteralStringPredicate([
    'STRING',
    'NUMERIC',
    'PERSON_OF_CONTACT'
  ]),
  subfields: Predicates.or(
    Predicates.isUndefined,
    Predicates.every(
      Predicates.hasShape({
        fieldName: Predicates.isString,
        fieldValue: Predicates.isString,
        unique: Predicates.isBoolean,
        type: Predicates.getLiteralStringPredicate([
          'CONTACT_NAME',
          'PHONE',
          'EMAIL'
        ])
      })
    )
  )
})

interface MetadataValue {
  readonly name: string
  readonly value: string
  readonly unique: boolean
  readonly type: MetadataFieldType
  readonly subfields?: Array<{
    readonly name: string
    readonly value: string
    readonly unique: boolean
    readonly type: MetadataSubfieldType
  }>
}

export function mapMetadata({
  fieldName,
  fieldValue,
  unique,
  type,
  subfields
}: MetadataEntity): MetadataValue {
  return {
    name: fieldName,
    value: fieldValue,
    unique,
    type,
    subfields: subfields
      ? subfields.map((subfield) => {
          return {
            name: subfield.fieldName,
            value: subfield.fieldValue,
            unique: subfield.unique,
            type: subfield.type
          }
        })
      : []
  }
}

function ApplicationTooltipContentBridge({
  name,
  url,
  importance,
  technologies,
  metadata = []
}: {
  readonly name: unknown
  readonly url: unknown
  readonly importance: unknown
  readonly technologies: unknown
  readonly metadata?: unknown
}) {
  return (
    <ApplicationTooltipContent
      name={Preconditions.checkArgument(Predicates.isString, name, 'name')}
      url={Preconditions.checkArgument(Predicates.isString, url, 'url')}
      importance={Preconditions.checkArgument(
        isApplicationImportance,
        importance,
        'importance'
      )}
      technologies={Preconditions.checkArgument(
        Predicates.or(
          Predicates.isUndefined,
          Predicates.every(Predicates.isString)
        ),
        technologies,
        'technologies'
      )}
      metadata={Preconditions.checkArgument(
        Predicates.every(isMetadataEntity),
        metadata,
        'metadata'
      ).map(mapMetadata)}
    />
  )
}

export const applicationTooltip = createNgReactBridgeDirective(
  'reactApplicationTooltip',
  [],
  ApplicationTooltipContentBridge,
  ['name', 'url', 'importance', 'technologies', 'metadata']
)

function ServerApplicationTooltipContentBridge({
  language,
  importance,
  url,
  metadata = []
}: {
  readonly language: string
  readonly importance: ApplicationImportance
  readonly url: string
  readonly metadata?: MetadataEntity[]
}) {
  return (
    <ServerApplicationTooltipContent
      language={Preconditions.checkArgument(
        Predicates.isString,
        language,
        'language'
      )}
      importance={Preconditions.checkArgument(
        isApplicationImportance,
        importance,
        'importance'
      )}
      url={Preconditions.checkArgument(Predicates.isString, url)}
      metadata={Preconditions.checkArgument(
        Predicates.every(isMetadataEntity),
        metadata,
        'metadata'
      ).map(mapMetadata)}
    />
  )
}

export const serverApplicationTooltip = createNgReactBridgeDirective(
  'reactServerApplicationTooltip',
  [],
  ServerApplicationTooltipContentBridge,
  ['language', 'importance', 'url', 'metadata']
)

const reactProtectApplicationTooltipName = 'reactProtectApplicationTooltip'

function ApplicationLinkWithProtectTooltipBridge({
  organizationId,
  applicationId,
  applicationName
}) {
  let checked: {
    readonly organizationId: string
    readonly applicationId: string
    readonly applicationName: string
  }

  try {
    checked = {
      organizationId: Preconditions.checkArgument(
        Predicates.isString,
        organizationId,
        'organizationId'
      ),
      applicationId: Preconditions.checkArgument(
        Predicates.isString,
        applicationId,
        'applicationId'
      ),
      applicationName: Preconditions.checkArgument(
        Predicates.isString,
        applicationName,
        'applicationName'
      )
    }
  } catch (e) {
    throw new Error(
      `Validation failed in ${reactProtectApplicationTooltipName}: ${e.toString()}`
    )
  }

  return (
    <ApplicationLinkWithTooltip
      applicationID={checked.applicationId}
      applicationName={checked.applicationName}
      organizationID={checked.organizationId}
    />
  )
}

export const protectApplicationTooltip = createNgReactBridgeDirective(
  reactProtectApplicationTooltipName,
  [],
  compose(injectIntl)(ApplicationLinkWithProtectTooltipBridge),
  ['applicationId', 'organizationId', 'applicationName']
)
